import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  ChevronRight,
  Sparkles,
  Activity,
  MessageSquare,
  CheckCircle2,
  Waves,
  Globe,
  Languages,
  Gauge,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  COUNTRY_DIAL_CODES,
  formatE164,
  initiateDemoCall,
  validatePhone,
} from "../../lib/demoCallApi";

const ROTATE_WORDS = [
  "Lead Qualification",
  "Customer Support",
  "Collections",
  "Appointment Booking",
  "WhatsApp Follow-ups",
  "Real Estate Sales",
];

const TRANSCRIPT = [
  { who: "agent", text: "Hi, this is Aria from Fawks AI. Is this Priya?" },
  { who: "caller", text: "Yes, speaking." },
  { who: "agent", text: "Great. I'm calling about the 3BHK in Bandra West." },
  { who: "caller", text: "Yes, I had enquired last week." },
  {
    who: "agent",
    text: "What budget are we working with — under ₹3.5 Cr okay?",
  },
  { who: "caller", text: "Yes, that works." },
];

const HERO_STATS = [
  { icon: Phone, value: "50,000+", label: "daily calls" },
  { icon: Languages, value: "70+", label: "languages supported" },
  { icon: Gauge, value: "<350ms", label: "voice latency" },
  { icon: ShieldCheck, value: "99.9%", label: "workflow adherence" },
];

const WaveBars = () => (
  <div className="flex items-end gap-[3px] h-4">
    {[0.1, 0.25, 0.05, 0.4, 0.6, 0.3, 0.5, 0.15].map((d, i) => (
      <span
        key={i}
        className="wave-bar block w-[3px] rounded-full bg-[#A999F0]"
        style={{ animationDelay: `${d}s`, height: "40%" }}
      />
    ))}
  </div>
);

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(1);
  const [country, setCountry] = useState("at");
  const [phone, setPhone] = useState("");
  const [isCalling, setIsCalling] = useState(false);

  const handleDemoCall = async () => {
    const validationError = validatePhone(country, phone);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const phoneNumber = formatE164(country, phone);
    setIsCalling(true);
    try {
      await initiateDemoCall(phoneNumber, country);
      toast.success(
        "Demo queued · Aria will call you in a few seconds. Keep your phone handy.",
      );
      setPhone("");
    } catch (err) {
      toast.error(
        err.message || "Could not start the demo call. Please try again.",
      );
    } finally {
      setIsCalling(false);
    }
  };

  useEffect(() => {
    const t = setInterval(
      () => setIdx((p) => (p + 1) % ROTATE_WORDS.length),
      2400,
    );
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (visible >= TRANSCRIPT.length) {
      const reset = setTimeout(() => setVisible(1), 4000);
      return () => clearTimeout(reset);
    }
    const t = setTimeout(() => setVisible((v) => v + 1), 1400);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#0b0a18] pt-24 pb-20 text-white lg:pt-28 lg:pb-24"
    >
      {/* Gradient blobs (`hero-blob` lets mobile CSS reduce paint cost) */}
      <div className="hero-blob pointer-events-none absolute -top-48 left-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#7D68C2]/40 blur-[140px]" />
      <div className="hero-blob pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[#A999F0]/25 blur-[140px]" />
      <div className="bg-grid-dark pointer-events-none absolute inset-0 opacity-25" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050816] to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0b0a18] to-transparent" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid items-start gap-10 lg:grid-cols-[1.1fr,1fr]"
        >
          {/* LEFT - copy */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-white/80 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-[#A999F0]" />
              Now live · Voice + WhatsApp + Chat agents
            </span>

            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-[2.6rem] font-extrabold leading-[1.02] tracking-tight sm:text-[3.4rem] lg:text-[3.9rem]"
            >
              The AI Agent Platform
              <br />
              to Scale{" "}
              {/* Stable rotating phrase: ghost reserves width on desktop,
                  block-level + fixed min-height on mobile to kill layout shift. */}
              <span className="relative inline-block whitespace-nowrap align-baseline max-md:mt-1 max-md:block max-md:min-h-[1.15em] max-md:whitespace-normal">
                {/* Invisible ghost — reserves the widest possible footprint */}
                <span aria-hidden="true" className="invisible">
                  Appointment Booking
                </span>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={idx}
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -12, opacity: 0 }}
                    transition={{ duration: 0.32, ease: "easeOut" }}
                    className="text-gradient-purple absolute inset-0 whitespace-nowrap max-md:whitespace-normal"
                  >
                    {ROTATE_WORDS[idx]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg">
              Deploy human-like AI voice, WhatsApp, and chat agents that qualify
              leads, answer questions, book meetings, update CRMs, and escalate
              to humans when needed.
            </p>

            {/* Demo phone input card */}
            <div
              data-testid="hero-demo-card"
              className="mt-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md"
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">
                  Experience a demo
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-white/70">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  </span>
                  Available now
                </span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select
                  value={country}
                  onValueChange={setCountry}
                  disabled={isCalling}
                >
                  <SelectTrigger
                    data-testid="hero-country-select"
                    className="w-full sm:w-[110px] rounded-xl border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.1]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1733] border-white/10 text-white">
                    {Object.entries(COUNTRY_DIAL_CODES).map(([code, dial]) => (
                      <SelectItem key={code} value={code}>
                        {dial} {code.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="hero-phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder={country === "in" ? "98765 43210" : "664 123456"}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleDemoCall();
                  }}
                  disabled={isCalling}
                  className="rounded-xl border-white/10 bg-white/[0.06] text-white placeholder:text-white/60"
                />
                <Button
                  data-testid="hero-try-btn"
                  onClick={handleDemoCall}
                  disabled={isCalling}
                  className="rounded-xl bg-gradient-purple px-5 font-semibold text-white shadow-[0_8px_30px_-8px_rgba(125,104,194,0.6)] hover:opacity-95 disabled:opacity-60"
                >
                  {isCalling ? "Calling…" : "Try Fawks AI"}
                  <Phone className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-white/60">
                AI call demo available for 2 minutes · No card required.
              </p>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
              {HERO_STATS.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col gap-1"
                  data-testid={`hero-stat-${s.value}`}
                >
                  <div className="flex items-center gap-1.5 text-[#A999F0]">
                    <s.icon className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-display text-2xl font-bold text-white">
                    {s.value}
                  </span>
                  <span className="text-[11px] uppercase tracking-wide text-white/65">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT - dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative animate-float"
          >
            <div className="absolute -inset-8 -z-10 rounded-[40px] bg-[#7D68C2]/25 blur-3xl" />

            {/* Main panel */}
            <div className="rounded-3xl border border-white/10 bg-[#131129]/90 p-5 shadow-[0_30px_80px_-30px_rgba(125,104,194,0.55)] backdrop-blur-xl">
              {/* Top bar */}
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-rose-400/80" />
                    <span className="h-2 w-2 rounded-full bg-amber-400/80" />
                    <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
                  </div>
                  <span className="ml-3 font-mono text-[11px] text-white/65">
                    fawks.ai / live-call / call_8214
                  </span>
                </div>
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-300">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  </span>
                  Live · 01:24
                </span>
              </div>

              {/* Agent status + waveform */}
              <div className="mt-4 grid grid-cols-3 gap-2.5">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/70">
                    <Activity className="h-3 w-3 text-[#A999F0]" />
                    Status
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-sm font-semibold text-white">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Speaking
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/70">
                    <Waves className="h-3 w-3 text-[#A999F0]" />
                    Audio
                  </div>
                  <div className="mt-2.5">
                    <WaveBars />
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/70">
                    <Sparkles className="h-3 w-3 text-[#A999F0]" />
                    Lead score
                  </div>
                  <div className="mt-1.5 flex items-baseline gap-1 text-sm font-semibold text-white">
                    <span className="font-display text-xl text-[#A999F0]">
                      87
                    </span>
                    <span className="text-[11px] text-white/60">/ 100</span>
                  </div>
                </div>
              </div>

              {/* Transcript */}
              <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4">
                <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider text-white/65">
                  <span>Transcript · English (IN)</span>
                  <span className="text-emerald-300">Real-time</span>
                </div>
                {/* All lines are always rendered so the box reserves its full
                    height from the first paint — no layout growth/shift as the
                    conversation "plays". Only opacity animates (transforms and
                    opacity don't affect layout), so height is constant. */}
                <div className="space-y-2 text-sm">
                  {TRANSCRIPT.map((line, i) => {
                    const on = i < visible;
                    return (
                      <motion.div
                        key={i}
                        initial={false}
                        animate={{ opacity: on ? 1 : 0.25 }}
                        transition={{ duration: 0.35 }}
                        className="flex gap-2"
                      >
                        <span
                          className={
                            "shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                            (line.who === "agent"
                              ? "bg-[#7D68C2]/25 text-[#C9BEF5]"
                              : "bg-white/10 text-white/60")
                          }
                        >
                          {line.who === "agent" ? "AI" : "Cx"}
                        </span>
                        <span
                          className={
                            line.who === "agent"
                              ? "text-white"
                              : "text-white/65"
                          }
                        >
                          {line.text}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Footer rail */}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-emerald-200">
                  <CheckCircle2 className="h-3 w-3" /> CRM synced · HubSpot
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#A999F0]/30 bg-[#7D68C2]/15 px-2.5 py-1 text-[#C9BEF5]">
                  <MessageSquare className="h-3 w-3" /> WhatsApp follow-up sent
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-white/70">
                  <Globe className="h-3 w-3" /> Disposition · Hot Lead
                </span>
              </div>
            </div>

            {/* Floating mini card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-6 -left-6 w-56 rounded-2xl border border-white/10 bg-[#1a1733]/95 p-3.5 shadow-2xl backdrop-blur-xl"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-purple">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/70">
                    Outbound campaign
                  </div>
                  <div className="text-sm font-semibold text-white">
                    1,284 calls · live
                  </div>
                </div>
              </div>
              <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-white/10">
                <div className="animate-progress-pulse h-full bg-gradient-purple" />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
